import React, { useState } from 'react';
import { X } from 'lucide-react';

const Sidebar = ({ show = false, setShow = () => {} }) => {
  const [price, setPrice] = useState(1300);
  const [prevPrice, setPrevPrice] = useState(1300);
  const [changeType, setChangeType] = useState(null);

  const handleChange = (e) => {
    setPrice(Number(e.target.value));
  };

  const handleFilter = () => {
    if (price > prevPrice) {
      setChangeType('increase');
    } else if (price < prevPrice) {
      setChangeType('decrease');
    } else {
      setChangeType(null);
    }
    setPrevPrice(price);
  };

  const ColorFilter = () => {
    const [selectedColor, setSelectedColor] = useState(null);
    const colors = [
      ['Apple Red', '#ef4444', 12],
      ['Bio Blue', '#1e3a8a', 9],
      ['Sweet Orange', '#fb923c', 15],
      ['Blue', '#60a5fa', 18],
      ['Green', '#065f46', 10],
      ['Pink', '#ec4899', 7],
      ['Red', '#dc2626', 14],
      ['Black', '#000000', 20],
      ['White', '#e5e7eb', 11],
      ['Yellow', '#facc15', 8],
    ];

    return (
      <div>
        <h3 className="font-semibold mb-3">Filter by Color</h3>
        <ul className="space-y-3 text-sm text-black">
          {colors.map(([name, color, qty], i) => {
            const isSelected = selectedColor === name;
            return (
              <li
                key={i}
                onClick={() => setSelectedColor(name)}
                className={`flex justify-between items-center px-2 py-1 rounded cursor-pointer transition 
                  ${isSelected ? 'bg-gray-100 ring-1 ring-red-400' : 'hover:bg-gray-50'}`}
              >
                <div className="flex items-center gap-3">
                  <span
                    className="w-4 h-4 rounded-full border border-gray-300"
                    style={{ backgroundColor: color }}
                  ></span>
                  <span>{name}</span>
                </div>
                <span className="text-gray-500">({qty})</span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <>
      {/* Overlay for Mobile */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-40 z-40 md:hidden ${show ? 'block' : 'hidden'}`}
        onClick={() => setShow(false)}
      ></div>

      {/* Sidebar Panel */}
      <div
        className={`
          fixed top-0 left-0 h-full w-72 bg-white z-50 md:z-40 shadow-md transform transition-transform duration-300
          ${show ? 'translate-x-0' : '-translate-x-full'}
          md:relative md:translate-x-0 md:block md:w-64
        `}
      >
          {/* Always-visible close button for mobile */}
        <div className="md:hidden sticky top-0 bg-white z-50 md:z-40 flex justify-end p-4 shadow-sm">
          <button onClick={() => setShow(false)} className="text-gray-600 text-xl focus:outline-none">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Scrollable content */}
        <div
          className="sticky overflow-y-auto px-4 pr-2"
          style={{
            top: '90px',
            height: 'calc(100vh - 60px)',
          }}
        >
          <div className="space-y-6 pb-8">
            {/* Categories */}
            <div>
              <h3 className="font-semibold mb-2">Product Categories</h3>
              <ul className="text-sm text-black space-y-2">
                {[
                  'Men', 'Women', 'Bags', 'Belts', 'Shoes', 'Outerwear',
                  'Cargo Trousers', 'Accessories', 'Baby', 'Kids',
                  'Wallets', 'Watches',
                ].map((cat, i) => (
                  <li key={i} className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <input type="checkbox" id={cat} />
                      <label htmlFor={cat}>{cat}</label>
                    </div>
                    {(cat === 'Men' || cat === 'Women') && (
                      <span className="text-lg text-gray-500 font-semibold cursor-pointer">+</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <hr className="border-gray-300" />

            {/* Price Filter */}
            <div className="p-3">
              <h3 className="font-semibold mb-2">Filter by Price</h3>
              <div className="flex justify-between items-center text-sm text-gray-600 mb-2">
                <span className="font-medium text-gray-800">Price: ₹0 — ₹{price}</span>
                <button
                  onClick={handleFilter}
                  className="bg-gray-200 px-3 py-1 rounded text-xs font-semibold hover:bg-gray-300"
                >
                  FILTER
                </button>
              </div>

              <div className="relative h-6">
                <div className="absolute top-1/2 left-0 w-full h-1 bg-red-400 transform -translate-y-1/2 z-0 rounded"></div>
                <input
                  type="range"
                  min="0"
                  max="1300"
                  value={price}
                  onChange={handleChange}
                  className="w-full appearance-none bg-transparent 
             [&::-webkit-slider-runnable-track]:bg-white 
             [&::-moz-range-track]:bg-white
             sm:[&::-webkit-slider-runnable-track]:bg-white
             sm:[&::-moz-range-track]:bg-white
             slider-thumb-red"
                />
                <span className="absolute top-1/2 left-0 transform -translate-y-1/2 w-3 h-3 bg-red-600 rounded-full z-20"></span>
                <span className="absolute top-1/2 right-0 transform -translate-y-1/2 w-3 h-3 bg-red-600 rounded-full z-20"></span>
              </div>

              {changeType && (
                <p className="mt-2 text-sm font-medium text-gray-700">
                  Price {changeType === 'increase' ? 'increased' : 'decreased'} to ₹{price}
                </p>
              )}
            </div>

            <hr className="border-gray-300" />

            <ColorFilter />

            <hr className="border-gray-300" />

            {/* Size Filter */}
            <div>
              <h3 className="font-semibold mb-2">Filter by Size</h3>
              <ul className="space-y-3 text-sm">
                {[
                  ['XXS', 5], ['XS', 8], ['S', 14], ['M', 20], ['L', 17], ['XL', 10], ['XXL', 6],
                ].map(([size, qty], i) => (
                  <li key={i} className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <input type="checkbox" id={`size-${size}`} className="w-4 h-4 accent-gray-700" />
                      <label htmlFor={`size-${size}`} className="cursor-pointer">
                        {size}
                      </label>
                    </div>
                    <span className="text-gray-500">({qty})</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
